import React, { createElement, isValidElement } from 'react';
import { ProtectedComponent } from '@/main/ProtectedComponents';
import type { TelemetryRouteHandle } from '@seeqdev/telemetry';

/**
 * App-owned route handle, following the React Router `handle` convention.
 * Extends the telemetry contract; add new fields here (breadcrumbs, titles,
 * ...) without touching the telemetry package — telemetry only reads
 * `routeName` and ignores the rest.
 */
export type AppRouteHandle = TelemetryRouteHandle;

interface PageOptions {
  /**
   * Route handle, passed through to the <Route> verbatim — page() never
   * constructs or merges it, so non-telemetry fields are never occluded.
   * Required: every page must declare a stable `routeName`, so omission is a
   * compile error. Routes that legitimately carry no handle (layout/grouping
   * routes, child tabs inheriting the parent page's name) don't use page().
   */
  handle: AppRouteHandle;
  /**
   * Access control — required, so a route's access level is always stated
   * inline and omission is a compile error (no silent-public foot-gun):
   * - 'public': no auth at all; only for routes reachable before login
   * - 'login': logged-in users only
   * - predicate: logged-in AND predicate (same semantics as ProtectedComponent)
   */
  permissions: 'public' | 'login' | (() => boolean);
}

/**
 * Builds the `{ element, handle }` props for a page <Route>, pairing the route
 * name used to dimension telemetry with the access level guarding the page so
 * the two are declared together and cannot drift apart.
 *
 * @example
 * // Public page (reachable before login):
 * <Route path="login" {...page(Login, { handle: { routeName: 'login' }, permissions: 'public' })} />
 *
 * @example
 * // Login-required page:
 * <Route path="ai" {...page(AiAssistant, { handle: { routeName: 'ai-assistant' }, permissions: 'login' })} />
 *
 * @example
 * // Permission-gated page:
 * <Route path="license" {...page(License, { handle: { routeName: 'license' }, permissions: isTrustedUser })} />
 *
 * @example
 * // One name shared across many paths (hoist the handle so they cannot drift):
 * const WORKSHEET_HANDLE: AppRouteHandle = { routeName: 'worksheet' };
 * worksheetRoutePaths.map((path) => (
 *   <Route key={path} path={path} {...page(WorksheetWrapper, { handle: WORKSHEET_HANDLE, permissions: 'login' })} />
 * ));
 *
 * @example
 * // Pages that need props take a ready element instead of a component type:
 * <Route path="load-error" {...page(<LoadError header="..." />, { handle: { routeName: 'load-error' }, permissions: 'public' })} />
 *
 * @example
 * // The handle stays open for non-telemetry route data:
 * page(HomeScreen, { handle: { routeName: 'home', breadcrumb: 'Home' }, permissions: 'login' })
 *
 * The helper is a convenience, not a requirement: `handle` remains the native
 * Route prop, so `<Route handle={{ routeName: 'forbidden' } satisfies AppRouteHandle} ... />`
 * is read by telemetry just the same.
 */
export function page(component: React.ComponentType | React.ReactElement, { handle, permissions }: PageOptions) {
  const content = isValidElement(component) ? component : createElement(component);
  if (permissions === 'public') {
    return { handle, element: <React.Suspense>{content}</React.Suspense> };
  }
  return {
    handle,
    element: <ProtectedComponent permissions={permissions === 'login' ? undefined : permissions} component={content} />,
  };
}
